/**
 * Photo Upload Enhancements
 * Adds error handling, progress indicators, load timeouts, accessibility,
 * and broken image fallbacks to the photo upload workflow.
 *
 * This script works alongside the existing 1r-theme.js without modifying it.
 * It uses event delegation and jQuery AJAX hooks to enhance the upload UX.
 */
(function() {
  'use strict';

  // =========================================================================
  // 1. TOAST NOTIFICATION SYSTEM (Task 2a)
  // =========================================================================
  var toastContainer = null;

  function createToastContainer() {
    if (toastContainer) return toastContainer;
    toastContainer = document.createElement('div');
    toastContainer.id = 'upload-toast-container';
    toastContainer.setAttribute('role', 'status');
    toastContainer.setAttribute('aria-live', 'polite');
    toastContainer.setAttribute('aria-atomic', 'true');
    toastContainer.style.cssText =
      'position:fixed;top:20px;right:20px;z-index:99999;display:flex;flex-direction:column;gap:10px;max-width:380px;width:calc(100% - 40px);pointer-events:none;';
    document.body.appendChild(toastContainer);
    return toastContainer;
  }

  function showToast(message, type, options) {
    type = type || 'error';
    options = options || {};
    var container = createToastContainer();

    var toast = document.createElement('div');
    toast.className = 'upload-toast upload-toast--' + type;
    toast.style.cssText =
      'pointer-events:auto;padding:14px 18px;border-radius:8px;font-size:14px;line-height:1.4;' +
      'box-shadow:0 4px 12px rgba(0,0,0,0.15);display:flex;align-items:flex-start;gap:10px;' +
      'animation:uploadToastIn 0.3s ease;font-family:inherit;';

    var colors = {
      error: 'background:#fef2f2;color:#991b1b;border:1px solid #fecaca;',
      success: 'background:#f0fdf4;color:#166534;border:1px solid #bbf7d0;',
      warning: 'background:#fffbeb;color:#92400e;border:1px solid #fde68a;',
      info: 'background:#eff6ff;color:#1e40af;border:1px solid #bfdbfe;'
    };
    toast.style.cssText += (colors[type] || colors.info);

    var icons = { error: '\u26A0', success: '\u2713', warning: '\u26A0', info: '\u2139' };
    var content = '<span style="flex-shrink:0;font-size:16px;">' + (icons[type] || '') + '</span>';
    content += '<span style="flex:1;">' + message + '</span>';

    if (options.retry) {
      content += '<button class="upload-toast__retry" style="' +
        'flex-shrink:0;padding:4px 12px;border-radius:4px;border:1px solid currentColor;' +
        'background:transparent;color:inherit;cursor:pointer;font-size:12px;white-space:nowrap;">' +
        'Retry</button>';
    }

    content += '<button class="upload-toast__close" style="' +
      'flex-shrink:0;background:none;border:none;color:inherit;cursor:pointer;font-size:18px;' +
      'line-height:1;padding:0;opacity:0.6;" aria-label="Dismiss">&times;</button>';

    toast.innerHTML = content;

    // Close button
    toast.querySelector('.upload-toast__close').addEventListener('click', function() {
      dismissToast(toast);
    });

    // Retry button
    var retryBtn = toast.querySelector('.upload-toast__retry');
    if (retryBtn && options.retry) {
      retryBtn.addEventListener('click', function() {
        dismissToast(toast);
        options.retry();
      });
    }

    container.appendChild(toast);

    // Auto-dismiss after duration
    var duration = options.duration || (type === 'error' ? 8000 : 5000);
    setTimeout(function() { dismissToast(toast); }, duration);

    return toast;
  }

  function dismissToast(toast) {
    if (!toast || !toast.parentNode) return;
    toast.style.animation = 'uploadToastOut 0.3s ease forwards';
    setTimeout(function() {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 300);
  }

  // Inject toast animation CSS
  var toastStyle = document.createElement('style');
  toastStyle.textContent =
    '@keyframes uploadToastIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}' +
    '@keyframes uploadToastOut{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(-10px)}}';
  document.head.appendChild(toastStyle);


  // =========================================================================
  // 2. UPLOAD PROGRESS INDICATOR (Task 2b)
  // =========================================================================
  var progressBar = null;

  function createProgressBar(parentEl) {
    removeProgressBar();
    var wrapper = document.createElement('div');
    wrapper.className = 'upload-progress';
    wrapper.setAttribute('role', 'progressbar');
    wrapper.setAttribute('aria-valuemin', '0');
    wrapper.setAttribute('aria-valuemax', '100');
    wrapper.setAttribute('aria-valuenow', '0');
    wrapper.setAttribute('aria-label', 'Upload progress');
    wrapper.style.cssText =
      'width:100%;height:6px;background:#e5e7eb;border-radius:3px;overflow:hidden;margin:10px 0;';

    var bar = document.createElement('div');
    bar.className = 'upload-progress__bar';
    bar.style.cssText =
      'width:0%;height:100%;background:#22c55e;border-radius:3px;transition:width 0.2s ease;';

    wrapper.appendChild(bar);
    progressBar = { wrapper: wrapper, bar: bar };

    if (parentEl) {
      parentEl.insertBefore(wrapper, parentEl.firstChild);
    }
    return progressBar;
  }

  function updateProgressBar(percent) {
    if (!progressBar) return;
    var p = Math.min(100, Math.max(0, percent));
    progressBar.bar.style.width = p + '%';
    progressBar.wrapper.setAttribute('aria-valuenow', String(Math.round(p)));
  }

  function removeProgressBar() {
    if (progressBar && progressBar.wrapper.parentNode) {
      progressBar.wrapper.parentNode.removeChild(progressBar.wrapper);
    }
    progressBar = null;
  }


  // =========================================================================
  // 3. INTERCEPT CLOUDINARY UPLOADS (Tasks 2a + 2b)
  // =========================================================================
  function setupAjaxInterceptor() {
    if (typeof jQuery === 'undefined' && typeof $ === 'undefined') return;
    var jq = typeof jQuery !== 'undefined' ? jQuery : $;

    jq.ajaxPrefilter(function(options, originalOptions, jqXHR) {
      if (!options.url || options.url.indexOf('api.cloudinary.com') === -1) return;

      // Find the modal footer to attach progress bar
      var modalFooter = document.querySelector('.modal--upload.is-active .modal-footer') ||
                        document.querySelector('#upload-modal .modal-footer');
      if (modalFooter) {
        createProgressBar(modalFooter);
      }

      // Override xhr to add upload progress
      var originalXhr = options.xhr;
      options.xhr = function() {
        var xhr = originalXhr ? originalXhr() : new XMLHttpRequest();
        if (xhr.upload) {
          xhr.upload.addEventListener('progress', function(e) {
            if (e.lengthComputable) {
              var percent = (e.loaded / e.total) * 100;
              updateProgressBar(percent);
            }
          });
        }
        return xhr;
      };

      // Enhanced error handling
      var originalError = options.error;
      options.error = function(xhr, status, error) {
        removeProgressBar();

        var message = 'Upload failed. ';
        if (status === 'timeout') {
          message += 'The upload timed out. Please check your connection and try again.';
        } else if (xhr.status === 0) {
          message += 'No network connection. Please check your internet and try again.';
        } else if (xhr.status === 413) {
          message += 'The image is too large. Please use a smaller photo.';
        } else if (xhr.status >= 500) {
          message += 'The upload server is temporarily unavailable. Please try again in a moment.';
        } else {
          message += 'Please try again or email your photo to <a href="mailto:support@wearfelicity.com">support@wearfelicity.com</a>';
        }

        showToast(message, 'error', {
          duration: 10000,
          retry: function() {
            var uploadInput = document.getElementById('upload-file');
            if (uploadInput) {
              var changeEvent = new Event('change', { bubbles: true });
              Object.defineProperty(changeEvent, 'target', {
                writable: false,
                value: uploadInput
              });
            }
            showToast('Please select your photo again to retry.', 'info');
          }
        });

        if (originalError) originalError.call(this, xhr, status, error);
      };

      // Success cleanup
      var originalSuccess = options.success;
      options.success = function() {
        removeProgressBar();
        if (originalSuccess) originalSuccess.apply(this, arguments);
      };

      // Done cleanup
      jqXHR.always(function() {
        setTimeout(removeProgressBar, 500);
      });
    });
  }


  // =========================================================================
  // 4. FILE VALIDATION BEFORE UPLOAD (Task 2a supplement)
  // =========================================================================
  function setupFileValidation() {
    document.addEventListener('change', function(e) {
      var input = e.target;
      if (!input || input.type !== 'file' || !input.classList.contains('btn__file-picker')) return;

      var file = input.files && input.files[0];
      if (!file) return;

      // Validate file type
      var validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/heic', 'image/heif'];
      var isValidType = validTypes.some(function(type) { return file.type === type; }) ||
                        file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|heic|heif)$/);

      if (!isValidType) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showToast('Unsupported file type. Please upload a JPG, PNG, GIF, or HEIC image.', 'error');
        input.value = '';
        return;
      }

      // Validate file size (50MB max)
      var maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showToast('Image is too large (max 50MB). Please use a smaller photo.', 'error');
        input.value = '';
        return;
      }
    }, true); // Capture phase to run before other handlers
  }


  // =========================================================================
  // 5. PINTURA LOAD TIMEOUT (Task 2c)
  // =========================================================================
  function setupPinturaTimeout() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          // Detect when editorLoading spinner appears
          if (node.classList && node.classList.contains('editorLoading') ||
              node.querySelector && node.querySelector('.editorLoading')) {
            startLoadTimeout();
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    var loadTimer = null;

    function startLoadTimeout() {
      clearTimeout(loadTimer);
      loadTimer = setTimeout(function() {
        // Check if Pintura actually rendered its UI (more than just the loading spinner)
        var root = document.querySelector('.PinturaRoot');
        var pinturaLoaded = root && root.children.length > 1;
        if (pinturaLoaded) return; // Editor loaded fine, do nothing

        var loading = document.querySelector('.editorLoading');
        if (loading && loading.parentNode) {
          loading.innerHTML =
            '<div style="text-align:center;padding:20px;color:#666;">' +
            '<p style="font-size:16px;margin-bottom:10px;">The photo editor is taking too long to load.</p>' +
            '<p style="font-size:14px;margin-bottom:15px;">Please try refreshing the page, or email your photo to ' +
            '<a href="mailto:support@wearfelicity.com" style="color:#22c55e;">support@wearfelicity.com</a></p>' +
            '<button onclick="location.reload()" style="' +
            'padding:8px 20px;border-radius:4px;border:1px solid #333;background:#333;color:#fff;cursor:pointer;">' +
            'Refresh Page</button>' +
            '</div>';
          loading.style.display = 'flex';
          loading.style.opacity = '1';
          loading.style.background = '#fff';
        }
      }, 10000);
    }

    // Clear timeout when editor loads successfully
    document.addEventListener('click', function(e) {
      if (e.target && (e.target.id === 'upload-save' || e.target.id === 'upload-save-multi')) {
        clearTimeout(loadTimer);
      }
    });
  }


  // =========================================================================
  // 6. ACCESSIBILITY ENHANCEMENTS (Task 5c)
  // =========================================================================
  function setupAccessibility() {
    // Add aria-live region for status updates
    var statusRegion = document.createElement('div');
    statusRegion.id = 'upload-status-announcer';
    statusRegion.setAttribute('role', 'status');
    statusRegion.setAttribute('aria-live', 'polite');
    statusRegion.setAttribute('aria-atomic', 'true');
    statusRegion.className = 'visually-hidden';
    statusRegion.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;';
    document.body.appendChild(statusRegion);

    // Announce upload status changes
    function announce(message) {
      statusRegion.textContent = '';
      setTimeout(function() { statusRegion.textContent = message; }, 100);
    }

    // Watch for modal open/close to manage focus and announce status
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;
        var target = mutation.target;
        if (!target.classList.contains('modal--upload')) return;

        if (target.classList.contains('is-active')) {
          // Modal opened
          target.setAttribute('role', 'dialog');
          target.setAttribute('aria-modal', 'true');
          var title = target.querySelector('.modal-title');
          if (title) {
            if (!title.id) title.id = 'modal-title-' + Math.random().toString(36).substr(2, 6);
            target.setAttribute('aria-labelledby', title.id);
          }
          announce('Photo editor opened. ' + (title ? title.textContent : ''));

          // Focus trap setup
          setupFocusTrap(target);
        } else {
          // Modal closed
          announce('Photo editor closed.');
          removeFocusTrap(target);
        }
      });
    });

    document.querySelectorAll('.modal--upload').forEach(function(modal) {
      observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
    });

    // Add aria-busy to loading spinners
    var spinnerObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('editorLoading')) {
            node.setAttribute('aria-busy', 'true');
            node.setAttribute('aria-label', 'Loading photo editor');
            announce('Loading photo editor, please wait.');
          }
        });
      });
    });
    spinnerObserver.observe(document.body, { childList: true, subtree: true });

    // Announce upload button state changes
    document.addEventListener('click', function(e) {
      var btn = e.target;
      if (btn && (btn.id === 'upload-save' || btn.id === 'upload-save-multi')) {
        announce('Uploading your photo, please wait.');
      }
    });

    // Add aria-describedby for file inputs
    document.querySelectorAll('.btn__file-picker').forEach(function(input) {
      var desc = document.createElement('span');
      desc.id = 'file-picker-desc-' + Math.random().toString(36).substr(2, 6);
      desc.className = 'visually-hidden';
      desc.textContent = 'Accepted formats: JPG, PNG, GIF, HEIC. Maximum size: 50MB.';
      desc.style.cssText =
        'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;';
      input.parentNode.appendChild(desc);
      input.setAttribute('aria-describedby', desc.id);
    });
  }

  // Focus trap for modals
  var _trapListener = null;
  function setupFocusTrap(modal) {
    removeFocusTrap(modal);
    var focusable = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    _trapListener = function(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    modal.addEventListener('keydown', _trapListener);
    setTimeout(function() { first.focus(); }, 100);
  }

  function removeFocusTrap(modal) {
    if (_trapListener) {
      modal.removeEventListener('keydown', _trapListener);
      _trapListener = null;
    }
  }


  // =========================================================================
  // 7. BROKEN IMAGE FALLBACK (Task 5d)
  // =========================================================================
  function setupBrokenImageFallback() {
    // Use event delegation for image error events
    document.addEventListener('error', function(e) {
      var img = e.target;
      if (img.tagName !== 'IMG') return;

      // Only handle photo upload preview images
      var isUploadPreview = img.id === 'upload-preview' ||
                            img.classList.contains('upload__preview') ||
                            img.closest('.filestack-photo') ||
                            img.closest('.photo-preview-card') ||
                            (img.src && img.src.indexOf('res.cloudinary.com') > -1);

      if (!isUploadPreview) return;
      if (img.dataset.fallbackApplied) return;

      img.dataset.fallbackApplied = 'true';
      img.alt = 'Photo unavailable';
      img.style.cssText += 'background:#f3f4f6;display:flex;align-items:center;justify-content:center;min-height:60px;';

      // Create placeholder SVG
      img.src = 'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" ' +
        'stroke="%239ca3af" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/>' +
        '<circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>'
      );
    }, true);
  }


  // =========================================================================
  // 8. FILEPOND PET PORTRAIT PREVIEW (Product Page)
  //    Handled by inline script in product-form.liquid
  // =========================================================================


  // =========================================================================
  // INITIALIZATION
  // =========================================================================
  function init() {
    setupAjaxInterceptor();
    setupFileValidation();
    setupPinturaTimeout();
    setupAccessibility();
    setupBrokenImageFallback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
